import React, { useState, useEffect } from 'react';
import { Shield, Award, Users, TrendingUp, Clock, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import PricingSection from '../components/PricingSection';
import TestimonialSlider from '../components/TestimonialSlider';
import ProcessSlider from '../components/ProcessSlider';
import ContactPopup from '../components/ContactPopup';
import HeroForm from '../components/HeroForm';
import Counter from '../components/Counter';
import SEO from '../components/SEO';
import ResponsiveCardSlider from '../components/ResponsiveCardSlider';

const Home = () => {
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      icon: Shield,
      title: 'Comprehensive Protection',
      description: 'Safeguard your brand with federal trademark registration and ongoing monitoring services.'
    },
    {
      icon: Award,
      title: 'Expert Guidance',
      description: 'Our experienced team provides professional support throughout the entire registration process.'
    },
    {
      icon: Users,
      title: 'Dedicated Support',
      description: 'Get personalized assistance from your dedicated case manager from start to finish.'
    },
    {
      icon: TrendingUp,
      title: 'High Success Rate',
      description: 'We maintain an exceptional approval rate with comprehensive search and application review.'
    },
    {
      icon: Clock,
      title: 'Fast Processing',
      description: 'Expedited filing services ensure your trademark application is submitted promptly.'
    },
    {
      icon: CheckCircle,
      title: 'Complete Service',
      description: 'From initial search to final registration, we handle every aspect of trademark protection.'
    }
  ];

  const stats = [
    { value: <Counter end={10000} suffix="+" />, label: 'Trademarks Registered' },
    { value: <Counter end={98} suffix="%" />, label: 'Client Satisfaction' },
    { value: <Counter end={15} suffix="+" />, label: 'Years Experience' },
    { value: '24/7', label: 'Support Available' }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Professional Trademark Registration & IP Protection Services"
        description="Protect your brand with expert trademark registration services. 98% success rate, 10,000+ trademarks registered. Fast filing, comprehensive protection, and ongoing support."
        keywords="trademark registration, brand protection, USPTO filing, intellectual property, trademark search, trademark attorney, IP protection"
      />
      
      {/* Hero Section with Form */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16 sm:py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1758518731462-d091b0b4ed0d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGxlZ2FsfGVufDB8fHx8MTc2MzUwMjYxOHww&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Content */}
            <div>
              <div className="inline-block bg-teal-600/20 backdrop-blur-sm border border-teal-400/30 rounded-full px-4 py-2 mb-6">
                <span className="text-teal-300 text-sm font-semibold">✓ Trusted by 10,000+ Businesses</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Protect Your Brand Legacy
              </h1>
              <p className="text-xl sm:text-2xl text-gray-200 mb-8 leading-relaxed">
                Secure your trademark with confidence. Expert guidance, comprehensive protection, and unmatched service.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-teal-600/20 backdrop-blur-sm rounded-lg flex items-center justify-center border border-teal-400/30">
                    <CheckCircle className="h-6 w-6 text-teal-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-lg">98% Approval Rate</p>
                    <p className="text-gray-300 text-sm">Industry-leading success rate</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-teal-600/20 backdrop-blur-sm rounded-lg flex items-center justify-center border border-teal-400/30">
                    <Shield className="h-6 w-6 text-teal-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-lg">Comprehensive Protection</p>
                    <p className="text-gray-300 text-sm">Federal trademark registration</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-teal-600/20 backdrop-blur-sm rounded-lg flex items-center justify-center border border-teal-400/30">
                    <Clock className="h-6 w-6 text-teal-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-lg">Fast Processing</p>
                    <p className="text-gray-300 text-sm">File within 24-48 hours</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Hero Form */}
            <HeroForm />
          </div>
        </div>
      </section>

      {/* Informative Section - Below Hero */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="flex flex-col justify-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Why Trademark Protection Matters
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Your brand is more than just a name or logo—it's your business identity, reputation, and promise to customers. Without proper trademark protection, you risk losing everything you've built to competitors or counterfeiters.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Federal trademark registration provides exclusive nationwide rights to your brand, legal presumption of ownership, and the ability to take action against infringement. It's an essential investment in your business's future.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-teal-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Exclusive Rights</h3>
                    <p className="text-gray-600">Secure nationwide protection for your brand identity</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-teal-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Legal Protection</h3>
                    <p className="text-gray-600">Establish legal grounds to stop infringers</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-teal-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Brand Value</h3>
                    <p className="text-gray-600">Increase your business valuation and credibility</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative flex items-center justify-center">
              <img
                src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxsZWdhbCUyMHNlcnZpY2VzfGVufDB8fHx8MTc2MzUwMzI2OHww&ixlib=rb-4.1.0&q=85"
                alt="Legal expertise"
                className="rounded-xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-teal-600 text-white p-6 rounded-lg shadow-xl max-w-xs">
                <p className="text-3xl font-bold mb-1"><Counter end={98} suffix="%" /></p>
                <p className="text-sm">Success rate in trademark approvals</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-teal-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold mb-2">{stat.value}</div>
                <div className="text-teal-100 text-sm sm:text-base">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Why Choose Us
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We provide comprehensive trademark services backed by expertise and dedication
            </p>
          </div>
          
          <ResponsiveCardSlider className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="p-6 hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400">
                  <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="h-7 w-7 text-teal-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </Card>
              );
            })}
          </ResponsiveCardSlider>
        </div>
      </section>

      {/* Innovation & Technology Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Modern Technology Meets Legal Expertise
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                We leverage cutting-edge technology and comprehensive databases to provide faster, more accurate trademark searches and filing services. Our proprietary system helps identify potential conflicts early, saving you time and money.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-white p-4 rounded-lg shadow">
                  <p className="text-3xl font-bold text-teal-600 mb-1"><Counter end={50} suffix="K+" /></p>
                  <p className="text-sm text-gray-600">Trademarks Searched</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                  <p className="text-3xl font-bold text-teal-600 mb-1"><Counter end={24} suffix="hrs" /></p>
                  <p className="text-sm text-gray-600">Average Response Time</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                  <p className="text-3xl font-bold text-teal-600 mb-1"><Counter end={100} suffix="%" /></p>
                  <p className="text-sm text-gray-600">Secure Processing</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                  <p className="text-3xl font-bold text-teal-600 mb-1">24/7</p>
                  <p className="text-sm text-gray-600">Customer Portal Access</p>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1573757056004-065ad36e2cf4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGlubm92YXRpb24lMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc2MzUwMzI2Mnww&ixlib=rb-4.1.0&q=85"
                alt="Technology and innovation"
                className="rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Industries We Serve
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Specialized trademark protection for businesses across all sectors
            </p>
          </div>

          <ResponsiveCardSlider className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {[
              { name: 'Technology', emoji: '💻' },
              { name: 'Healthcare', emoji: '🏥' },
              { name: 'Retail', emoji: '🛍️' },
              { name: 'Food & Beverage', emoji: '🍽️' },
              { name: 'Fashion', emoji: '👔' },
              { name: 'Entertainment', emoji: '🎬' },
              { name: 'Real Estate', emoji: '🏢' },
              { name: 'Finance', emoji: '💰' },
              { name: 'Education', emoji: '📚' },
              { name: 'Manufacturing', emoji: '🏭' },
              { name: 'Hospitality', emoji: '🏨' },
              { name: 'E-commerce', emoji: '📦' }
            ].map((industry, index) => (
              <Card key={index} className="p-4 text-center hover:shadow-lg transition-all duration-300 hover:scale-105 border-2 border-gray-200 hover:border-teal-400">
                <div className="text-4xl mb-2">{industry.emoji}</div>
                <p className="text-sm font-semibold text-slate-900">{industry.name}</p>
              </Card>
            ))}
          </ResponsiveCardSlider>
        </div>
      </section>

      {/* Success Stories Section */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHN1Y2Nlc3N8ZW58MHx8fHwxNzYzNTAzNDU1fDA&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Success by the Numbers
            </h2>
            <p className="text-xl text-teal-100">
              Real results from real businesses we've helped protect
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { number: <Counter end={10000} suffix="+" />, label: 'Trademarks Filed', icon: '📝' },
              { number: <Counter end={98} suffix="%" />, label: 'Approval Rate', icon: '✅' },
              { number: <Counter end={5000} suffix="+" />, label: 'Happy Clients', icon: '😊' },
              { number: <Counter end={15} suffix="+" />, label: 'Years Experience', icon: '🏆' },
              { number: '24/7', label: 'Support Available', icon: '💬' },
              { number: <Counter end={50} suffix="+" />, label: 'Industries Served', icon: '🌐' },
              { number: <Counter end={99} suffix="%" />, label: 'Client Satisfaction', icon: '⭐' },
              { number: <><Counter end={24} prefix="<" suffix="hrs" /></>, label: 'Response Time', icon: '⚡' }
            ].map((stat, index) => (
              <div key={index} className="text-center bg-white/10 backdrop-blur-sm p-6 rounded-lg hover:bg-white/20 transition-all duration-300">
                <div className="text-4xl mb-2">{stat.icon}</div>
                <div className="text-3xl sm:text-4xl font-bold mb-2">{stat.number}</div>
                <div className="text-teal-100 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us vs Others */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Why Businesses Choose Us
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              See how we compare to traditional law firms and other trademark services
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <Card className="p-8 border-2 border-gray-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center">Traditional Law Firms</h3>
              <ul className="space-y-4">
                {[
                  'High hourly rates ($300-$500/hr)',
                  'Complex billing structures',
                  'Limited availability',
                  'Formal, intimidating process',
                  'Weeks for responses',
                  'No dedicated portal'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-600">
                    <span className="text-red-500 flex-shrink-0 mt-1">✗</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="p-8 border-2 border-teal-500 shadow-xl relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-teal-600 text-white px-6 py-2 rounded-full text-sm font-bold">
                  Trademark Resolutions
                </span>
              </div>
              <h3 className="text-2xl font-bold text-teal-600 mb-6 text-center mt-4">Our Approach</h3>
              <ul className="space-y-4">
                {[
                  'Transparent flat-rate pricing',
                  'All-inclusive packages',
                  '24/7 customer portal access',
                  'Simple, friendly process',
                  'Same-day responses',
                  'Dedicated case manager'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-700">
                    <span className="text-teal-600 flex-shrink-0 mt-1 text-xl">✓</span>
                    <span className="font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Trust & Partnerships */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            <div className="flex flex-col justify-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Trusted by Thousands of Businesses
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                From startups to Fortune 500 companies, businesses trust us to protect their most valuable asset—their brand. Our commitment to excellence and client satisfaction has earned us recognition across the industry.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">🏅</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 mb-1">Industry Recognition</h3>
                    <p className="text-gray-600 text-sm">Awarded Best Trademark Service 2024</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">🔒</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 mb-1">Secure & Confidential</h3>
                    <p className="text-gray-600 text-sm">Bank-level encryption for all data</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">👥</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 mb-1">Expert Team</h3>
                    <p className="text-gray-600 text-sm">50+ trademark specialists on staff</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center">
              <div className="grid grid-cols-2 gap-4 w-full">
                <div className="flex flex-col gap-4">
                  <img
                    src="https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw1fHx0ZWFtd29ya3xlbnwwfHx8fDE3NjM1MDM0NjR8MA&ixlib=rb-4.1.0&q=85"
                    alt="Team collaboration"
                    className="rounded-lg shadow-lg hover:scale-105 transition-transform duration-300 h-48 object-cover"
                  />
                  <img
                    src="https://images.unsplash.com/photo-1527525443983-6e60c75fff46?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHx0ZWFtd29ya3xlbnwwfHx8fDE3NjM1MDM0NjR8MA&ixlib=rb-4.1.0&q=85"
                    alt="Team unity"
                    className="rounded-lg shadow-lg hover:scale-105 transition-transform duration-300 h-48 object-cover"
                  />
                </div>
                <div className="flex flex-col gap-4">
                  <img
                    src="https://images.unsplash.com/photo-1523287562758-66c7fc58967f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMHN1Y2Nlc3N8ZW58MHx8fHwxNzYzNTAzNDU1fDA&ixlib=rb-4.1.0&q=85"
                    alt="Business growth"
                    className="rounded-lg shadow-lg hover:scale-105 transition-transform duration-300 h-48 object-cover"
                  />
                  <img
                    src="https://images.unsplash.com/photo-1576267423048-15c0040fec78?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMHN1Y2Nlc3N8ZW58MHx8fHwxNzYzNTAzNDU1fDA&ixlib=rb-4.1.0&q=85"
                    alt="Success celebration"
                    className="rounded-lg shadow-lg hover:scale-105 transition-transform duration-300 h-48 object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section - 3 Steps New Design */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Our Simple 3-Step Process
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From search to protection in three streamlined steps
            </p>
          </div>

          <ResponsiveCardSlider className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="relative group">
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 z-10">
                <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl shadow-lg">
                  1
                </div>
              </div>
              <Card className="pt-10 pb-8 px-6 text-center hover:shadow-2xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400 bg-white group-hover:scale-105">
                <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-teal-600 transition-colors duration-300">
                  <svg className="w-10 h-10 text-teal-600 group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Comprehensive Search</h3>
                <p className="text-gray-600 leading-relaxed">
                  Our team performs an extensive search to identify potential conflicts and assess the viability of your trademark application.
                </p>
              </Card>
              {/* Connector Arrow - Hidden on mobile */}
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                <div className="text-teal-600 text-4xl">→</div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative group">
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 z-10">
                <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl shadow-lg">
                  2
                </div>
              </div>
              <Card className="pt-10 pb-8 px-6 text-center hover:shadow-2xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400 bg-white group-hover:scale-105">
                <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-teal-600 transition-colors duration-300">
                  <svg className="w-10 h-10 text-teal-600 group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Application Filing</h3>
                <p className="text-gray-600 leading-relaxed">
                  We prepare and file your trademark application with the USPTO, ensuring all documentation is accurate and complete.
                </p>
              </Card>
              {/* Connector Arrow - Hidden on mobile */}
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                <div className="text-teal-600 text-4xl">→</div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative group">
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 z-10">
                <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl shadow-lg">
                  3
                </div>
              </div>
              <Card className="pt-10 pb-8 px-6 text-center hover:shadow-2xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400 bg-white group-hover:scale-105">
                <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-teal-600 transition-colors duration-300">
                  <svg className="w-10 h-10 text-teal-600 group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Monitoring & Protection</h3>
                <p className="text-gray-600 leading-relaxed">
                  After approval, we monitor your trademark and provide ongoing support to maintain and protect your brand rights.
                </p>
              </Card>
            </div>
          </ResponsiveCardSlider>
        </div>
      </section>

      {/* Pricing Section */}
      <div id="pricing">
        <PricingSection onGetStarted={() => setShowPopup(true)} />
      </div>

      {/* Testimonials Section */}
      <TestimonialSlider />

      {/* Common Questions - Interactive */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Common Questions Answered
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Quick answers to help you understand the trademark process
            </p>
          </div>

          <ResponsiveCardSlider className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                question: 'How much does it cost?',
                answer: 'Our packages start at $45 plus government fees. Transparent pricing with no hidden costs.',
                icon: '💰'
              },
              {
                question: 'How long does it take?',
                answer: 'Filing takes 24-48 hours. Full registration typically takes 8-12 months through USPTO.',
                icon: '⏱️'
              },
              {
                question: 'Do I need a trademark?',
                answer: 'If you have a business name, logo, or slogan you want to protect, yes! Trademarks prevent others from using your brand.',
                icon: '🤔'
              },
              {
                question: 'What if my application is rejected?',
                answer: 'We provide expert support for office actions and work to overcome any objections from USPTO.',
                icon: '❌'
              },
              {
                question: 'Can I trademark internationally?',
                answer: 'Yes! After US registration, we can help you expand protection to other countries.',
                icon: '🌍'
              },
              {
                question: 'What\'s included in the price?',
                answer: 'Search, preparation, filing, and monitoring. Higher tiers include dedicated support and priority service.',
                icon: '📦'
              }
            ].map((item, index) => (
              <Card key={index} className="p-6 hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400 hover:scale-105">
                <div className="text-5xl mb-4">{item.icon}</div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">{item.question}</h3>
                <p className="text-gray-600 text-sm">{item.answer}</p>
              </Card>
            ))}
          </ResponsiveCardSlider>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?q=80&w=2070"
            alt="Success team"
            className="w-full h-full object-cover"
          />
          {/* Color Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-teal-600/95 via-teal-700/90 to-slate-800/95"></div>
        </div>

        {/* Content */}
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block bg-white/20 backdrop-blur-sm border border-white/30 rounded-full px-6 py-2 mb-6">
            <span className="text-white text-sm font-semibold">✓ Join 10,000+ Protected Brands</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight">
            Ready to Protect Your Brand?
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-3xl mx-auto leading-relaxed">
            Take the first step towards securing your intellectual property. Our expert team is ready to guide you through the entire process with professionalism and care.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => setShowPopup(true)}
              size="lg"
              className="bg-white text-teal-600 hover:bg-gray-100 px-10 py-7 text-lg font-bold shadow-2xl hover:shadow-3xl hover:scale-105 transition-all duration-300"
            >
              Get Started Now →
            </Button>
            <Button
              onClick={() => window.location.href = '/contact'}
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-teal-600 px-10 py-7 text-lg font-bold transition-all duration-300 backdrop-blur-sm"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      {showPopup && <ContactPopup onClose={() => setShowPopup(false)} />}
    </div>
  );
};

export default Home;